import { createContext } from "react";
import axios from "axios";
import { getAccessToken, takeSessionError } from "../api/auth";
import {
  isCognitoAuthRejection,
  markRetry,
  notifyAuthRejected,
  retryDelayMs,
  shouldRetryRequest,
  toApiError,
} from "./apiFailure";
import {
  attachOrgHeader,
  handleStaleLocation,
  handleStaleOrg,
  restartAtRoot,
} from "./orgRequestHooks";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

export const baseAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

baseAxios.interceptors.request.use(
  async (request) => {
    if (!request.headers.common["Authorization"]) {
      const token = await getAccessToken();
      request.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // JD-293: Send x-location-id header for multi-location context switching.
    // Skip for the login bootstrap endpoint to avoid cross-session stale-location issues.
    if (!request.url?.includes("/users/userAuthorization")) {
      try {
        const selectedLocation = sessionStorage.getItem("selectedLocationId");
        if (selectedLocation && selectedLocation !== "-1") {
          request.headers["x-location-id"] = selectedLocation;
        }
      } catch (e) { /* sessionStorage unavailable (incognito) */ }
    }

    // JD-424: a super admin acts inside one selected organization.
    attachOrgHeader(request);

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

baseAxios.interceptors.response.use(
  (response) => response, // Directly return successful responses.
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status ?? 0;

    // Dropped connections and gateway timeouts: retry safe reads only.
    // Creates and updates are left to the caller so a save is not sent twice.
    if (shouldRetryRequest(originalRequest, status)) {
      markRetry(originalRequest, status);
      await wait(retryDelayMs(originalRequest));
      return baseAxios(originalRequest);
    }

    // 401: ask Cognito for the current access token once, then retry.
    // A failed call does not delete the refresh token. Logout happens only
    // when Cognito itself rejects that refresh token.
    if (status === 401 && originalRequest && !originalRequest._authRetry) {
      originalRequest._authRetry = true;
      const token = await getAccessToken();
      if (token) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        originalRequest.headers.common = originalRequest.headers.common || {};
        originalRequest.headers.common.Authorization = `Bearer ${token}`;
        return baseAxios(originalRequest);
      }
      if (isCognitoAuthRejection(takeSessionError())) {
        notifyAuthRejected();
      }
    }

    if (error.response) {
      handleStaleOrg(
        error.response.status,
        error.response.data?.message,
        restartAtRoot
      ) ||
        handleStaleLocation(
          error.response.status,
          error.response.data?.message,
          restartAtRoot
        );
    }

    return Promise.reject(toApiError(error));
  }
);

export const BASE_URL = "https://d18devmarketplace.meevodev.com";
export const PAGE_SIZE = 200;
export const BASE_ENDPOINT = "https://d18devpub.meevodev.com/publicapi/v1";
export const APP_ID = "50add9b8-c523-40c5-b74c-961b6da3e45f";
export const APP_SECRET = "e6298efc-cb30-45ad-b7d6-3123e0b30b97";

export const COGNITO_POOL_ID = process.env.REACT_APP_COGNITO_POOL_ID;
export const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;

export const Context = createContext(null);
